import { useEffect, useState } from 'react';
import { FaStar, FaShieldAlt, FaFileContract, FaGlobe, FaCheckCircle, FaRegClock, FaArrowRight } from "react-icons/fa";
import { Link } from 'react-router-dom';
import LazyImg from '../../components/LazyImg';
import Meta from '../../components/Meta';
import Faq from '../../components/Faq';
import Testimonial from '../../components/Testimonial';
import headerImg from "../../assets/images/headerImg.png";
import RotatingCircle from '../../components/RotatingCircle/RotatingCircle';

export default function VATBody() {
  const [responsive, setResponsive] = useState(false);
  const PRIMARY_COLOR = '#1a2d4d';     // Navy - professional & trustworthy
  const SECONDARY_COLOR = '#2d5a8c';   // Professional blue accent
  const ACCENT_COLOR = '#f5a623';      // Warm orange - restrained use
  const GRAY_DARK = '#6b7280';         // Professional gray text

  useEffect(() => {
    const onResize = () => setResponsive(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fontSize = (big, small) => (responsive ? small : big);

  const faqItems = [
    {
      title: 'What is VAT?',
      content: <p>Value Added Tax (VAT) is a consumption tax collected at each stage of the supply chain. It's a tax on the value added at each level of production or distribution.</p>
    },
    {
      title: 'Who needs to register for VAT in Bangladesh?',
      content: <p>Any business with an annual turnover exceeding BDT 30 lakh (3 million) must register for VAT with the National Board of Revenue (NBR).</p>
    },
    {
      title: 'How long does the VAT registration process take?',
      content: <p>The VAT registration process typically takes 7-14 days from submission of complete documents, depending on NBR processing times.</p>
    },
    {
      title: 'What documents are required for VAT registration?',
      content: <p>Required documents include business registration certificate, trade license, NID/passport, bank account details, premises lease agreement, and tax identification number (TIN).</p>
    }
  ];

  return (
    <main style={{ maxWidth: "100%", backgroundColor: "#f5f5f5" }}>
      <Meta
        title="VAT Registration & Consultancy in Bangladesh | JK Associates"
        description="Expert VAT registration, online VAT filing, and comprehensive VAT consultancy services in Bangladesh. Simplify your VAT compliance with JK Associates."
        keywords="VAT Registration Bangladesh, VAT Online Filing, VAT Consultancy, VAT Services"
      />

      {/* HERO SECTION - PROFESSIONAL CLASSY DESIGN */}
      <section style={{
        background: `linear-gradient(135deg, rgba(26, 45, 77, 0.24) 0%, rgba(45, 89, 140, 0.24) 10%), url(${require('../../assets/images/vat_bg.jpg')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: PRIMARY_COLOR,
        padding: responsive ? '80px 20px 100px 20px' : '120px 40px 140px 40px',
        position: 'relative',
        overflow: 'hidden',
        minHeight: responsive ? 'auto' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        {/* Animated gradient orbs for visual depth */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(245, 166, 35, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          animation: 'float 12s ease-in-out infinite'
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '-15%',
          left: '-15%',
          width: '450px',
          height: '450px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          animation: 'float 15s ease-in-out infinite 2s'
        }}></div>

        <div style={{
          position: 'absolute',
          top: '50%',
          right: '-5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(45, 90, 140, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          animation: 'float 18s ease-in-out infinite 4s'
        }}></div>

        {/* Background overlay for better readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(26, 45, 77, 0.2) 0%, rgba(26, 45, 77, 0.4) 50%, rgba(26, 45, 77, 0.3) 100%)',
          pointerEvents: 'none'
        }}></div>

        <div style={{ maxWidth: '1200px', width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Top Tagline with animation */}
          <div style={{
            marginBottom: responsive ? '40px' : '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            animation: 'slideInDown 0.8s ease-out'
          }}>
            <span style={{
              fontSize: fontSize("14px", "12px"),
              fontWeight: '800',
              color: ACCENT_COLOR,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              ✨ Meet JK Associates.
            </span>
            <span style={{
              fontSize: fontSize("14px", "12px"),
              fontWeight: '600',
              color: '#ffffff',
              letterSpacing: '0.4px',
              opacity: 0.95
            }}>
              Your Expert VAT & Compliance Partner
            </span>
          </div>

          {/* Main Headline - Large Bold with animation */}
          <h1 style={{
            fontSize: responsive ? '44px' : '76px',
            fontWeight: '900',
            margin: '0 auto 50px auto',
            lineHeight: '1.1',
            letterSpacing: '-1.8px',
            color: '#ffffff',
            maxWidth: '1100px',
            textShadow: '0 8px 20px rgba(26, 45, 77, 0.4)',
            animation: 'slideInUp 1s ease-out 0.2s both',
            wordBreak: 'break-word'
          }}>
            Simplify Your VAT Compliance — Track, Report & Pay with Confidence
          </h1>

          {/* Four Feature Cards in Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: responsive ? '20px' : '24px',
            marginBottom: responsive ? '50px' : '70px',
            maxWidth: '1100px',
            margin: '0 auto 60px auto',
            animation: 'fadeIn 1s ease-out 0.4s both'
          }}>
            {[
              {
                icon: '⚡',
                title: 'Swift Registration',
                desc: '7-14 days with expert guidance'
              },
              {
                icon: '🔧',
                title: 'Automated Processing',
                desc: '100% NBR compliance ensured'
              },
              {
                icon: '📊',
                title: 'Complete Filing & Reporting',
                desc: 'Hassle-free documentation'
              },
              {
                icon: '👥',
                title: 'Expert Assistance',
                desc: '24/7 dedicated support team'
              }
            ].map((feature, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '18px',
                  padding: responsive ? '28px 20px' : '36px 28px',
                  border: '1.5px solid rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '14px',
                  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  cursor: 'pointer',
                  boxShadow: '0 16px 48px rgba(26, 45, 77, 0.15)',
                  animation: `slideUp 0.8s ease-out ${0.5 + i * 0.1}s both`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 24px 64px rgba(245, 166, 35, 0.25)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.borderColor = ACCENT_COLOR;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 16px 48px rgba(26, 45, 77, 0.15)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                }}
              >
                <div style={{
                  fontSize: '48px',
                  marginBottom: '6px',
                  transition: 'all 0.3s ease',
                  filter: 'drop-shadow(0 4px 8px rgba(26, 45, 77, 0.15))'
                }}>
                  {feature.icon}
                </div>
                <h4 style={{
                  fontSize: fontSize("17px", "15px"),
                  fontWeight: '800',
                  margin: '0 0 8px 0',
                  color: PRIMARY_COLOR,
                  letterSpacing: '0.3px'
                }}>
                  {feature.title}
                </h4>
                <p style={{
                  fontSize: fontSize("14px", "12px"),
                  color: '#5a6b7e',
                  margin: '0',
                  lineHeight: '1.6',
                  fontWeight: '500'
                }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Primary CTA Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: responsive ? '60px' : '80px',
            animation: 'slideUp 0.8s ease-out 0.6s both'
          }}>
            <button
              style={{
                backgroundColor: ACCENT_COLOR,
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                padding: responsive ? '16px 36px' : '18px 56px',
                fontSize: fontSize("16px", "14px"),
                fontWeight: '800',
                cursor: 'pointer',
                transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: '0 16px 48px rgba(245, 166, 35, 0.4)',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 24px 64px rgba(245, 166, 35, 0.6)';
                e.target.style.transform = 'translateY(-4px) scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = '0 16px 48px rgba(245, 166, 35, 0.4)';
                e.target.style.transform = 'translateY(0) scale(1)';
              }}
              onClick={() => window.open('https://api.whatsapp.com/send/?phone=+8801511404971&text=I%20am%20interested%20in%20VAT%20Registration%20and%20Consultancy%20services', '_blank')}
            >
              📞 Book Free Consultation <FaArrowRight size={18} />
            </button>
          </div>

          {/* Trust Section - Bottom */}
          <div style={{
            display: 'flex',
            flexDirection: responsive ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: responsive ? '18px' : '32px',
            paddingTop: '40px',
            borderTop: '1px solid rgba(255, 255, 255, 0.3)',
            animation: 'fadeIn 1s ease-out 0.8s both'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                fontSize: fontSize("13px", "11px"),
                fontWeight: '800',
                color: ACCENT_COLOR,
                textTransform: 'uppercase',
                letterSpacing: '0.6px'
              }}>
                ⭐ We've helped 500+
              </span>
              <span style={{
                fontSize: fontSize("13px", "11px"),
                color: '#ffffff',
                fontWeight: '600',
                opacity: 0.95
              }}>
                Companies Grow Successfully
              </span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              {['✓', '✓', '✓', '✓'].map((badge, i) => (
                <div
                  key={i}
                  style={{
                    width: '36px',
                    height: '36px',
                    background: `linear-gradient(135deg, ${[ACCENT_COLOR, '#4ECDC4', '#FF6B6B', '#6BCB77'][i]} 0%, ${[ACCENT_COLOR, '#4ECDC4', '#FF6B6B', '#6BCB77'][i]}dd 100%)`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: '14px',
                    boxShadow: `0 6px 16px rgba(${[245, 166, 35][0]}, ${[245, 166, 35][1]}, ${[245, 166, 35][2]}, 0.25)`,
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    transition: 'all 0.3s ease',
                    animation: `pop 0.6s ease-out ${1 + i * 0.1}s both`
                  }}
                >
                  {badge}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced CSS Animations */}
        <style>{`
          @keyframes slideInDown {
            from {
              opacity: 0;
              transform: translateY(-30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-30px);
            }
          }

          @keyframes pop {
            0% {
              transform: scale(0.5);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
        {/* CSS Animations */}
        <style>{`
    @keyframes float {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-30px);
      }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.8;
      }
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-4px);
      }
    }
                `}</style>
      </section>

      {/* TRUST SECTION - REDESIGNED */}
      <section style={{
        backgroundColor: '#f5f5f5',
        padding: responsive ? '50px 20px' : '80px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '-100px',
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, rgba(245, 166, 35, 0.08) 0%, transparent 70%)`,
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite',
          pointerEvents: 'none'
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '-50px',
          right: '-100px',
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, rgba(31, 37, 72, 0.05) 0%, transparent 70%)`,
          borderRadius: '50%',
          animation: 'float 10s ease-in-out infinite 1s',
          pointerEvents: 'none'
        }}></div>

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: 'rgba(245, 166, 35, 0.15)',
              padding: '12px 24px',
              borderRadius: '50px',
              border: `1.5px solid ${SECONDARY_COLOR}`,
              marginBottom: '20px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <span style={{
                color: SECONDARY_COLOR,
                fontSize: fontSize("12px", "11px"),
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                ⭐ TRUSTED BY INDUSTRY LEADERS
              </span>
            </div>

            <h2 style={{
              fontSize: fontSize("40px", "28px"),
              fontWeight: '700',
              color: PRIMARY_COLOR,
              margin: '0 0 16px 0',
              lineHeight: '1.3'
            }}>
              500+ Businesses Trust Us
            </h2>

            <p style={{
              fontSize: fontSize("16px", "14px"),
              color: '#666',
              margin: '0',
              maxWidth: '600px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Across diverse industries, our VAT expertise has successfully guided businesses to compliance and growth
            </p>
          </div>

          {/* Industry Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr 1fr' : 'repeat(5, 1fr)',
            gap: responsive ? '24px' : '20px',
            alignItems: 'stretch'
          }}>
            {[
              {
                icon: '🏭',
                name: 'Manufacturing',
                description: 'Factory & Production',
                color: '#FF6B6B',
                delay: '0s',
                clients: '120+'
              },
              {
                icon: '📦',
                name: 'Trading',
                description: 'B2B & Wholesale',
                color: '#4ECDC4',
                delay: '0.1s',
                clients: '150+'
              },
              {
                icon: '🛠️',
                name: 'Services',
                description: 'Consulting & Support',
                color: '#45B7D1',
                delay: '0.2s',
                clients: '100+'
              },
              {
                icon: '🚚',
                name: 'Export/Import',
                description: 'International Trade',
                color: '#96CEB4',
                delay: '0.3s',
                clients: '80+'
              },
              {
                icon: '🛍️',
                name: 'Retail',
                description: 'Stores & E-commerce',
                color: '#F7DC6F',
                delay: '0.4s',
                clients: '50+'
              }
            ].map((industry, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: responsive ? '24px 16px' : '32px 24px',
                  border: `2px solid #e8e8e8`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  textAlign: 'center',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `slideUp 0.6s ease-out ${industry.delay}`,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                  e.currentTarget.style.borderColor = industry.color;
                  e.currentTarget.style.boxShadow = `0 16px 32px ${industry.color}25, inset 0 1px 0 rgba(255,255,255,0.6)`;
                  e.currentTarget.style.backgroundColor = `${industry.color}08`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                {/* Gradient background accent */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${industry.color}, transparent)`,
                  transition: 'all 0.3s ease'
                }}></div>

                {/* Icon Circle */}
                <div style={{
                  width: '70px',
                  height: '70px',
                  backgroundColor: `${industry.color}15`,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '36px',
                  margin: '0 auto',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${industry.color}30`,
                  animation: `bounce 2s ease-in-out infinite ${industry.delay}`
                }}>
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(135deg, ${industry.color}20, transparent)`,
                    borderRadius: '12px'
                  }}></div>
                  <span style={{ position: 'relative', zIndex: 1 }}>
                    {industry.icon}
                  </span>
                </div>

                {/* Content */}
                <div>
                  <h3 style={{
                    fontSize: fontSize("16px", "14px"),
                    fontWeight: '700',
                    color: PRIMARY_COLOR,
                    margin: '0 0 6px 0',
                    letterSpacing: '0.3px'
                  }}>
                    {industry.name}
                  </h3>
                  <p style={{
                    fontSize: fontSize("13px", "12px"),
                    color: '#999',
                    margin: '0 0 12px 0',
                    fontWeight: '500'
                  }}>
                    {industry.description}
                  </p>

                  {/* Client Count Badge */}
                  <div style={{
                    display: 'inline-block',
                    backgroundColor: `${industry.color}15`,
                    color: industry.color,
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                    border: `1px solid ${industry.color}30`,
                    transition: 'all 0.3s ease'
                  }}>
                    {industry.clients} Clients
                  </div>
                </div>

                {/* Bottom accent line */}
                <div style={{
                  marginTop: '12px',
                  height: '2px',
                  background: `linear-gradient(90deg, ${industry.color}50, transparent)`,
                  borderRadius: '1px',
                  transition: 'all 0.3s ease'
                }}></div>

                {/* Checkmark indicator */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: industry.color,
                  fontWeight: '600',
                  opacity: 0.7,
                  transition: 'all 0.3s ease'
                }}>
                  <span style={{ fontSize: '14px' }}>✓</span>
                  <span>Fully Compliant</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced CSS Animations */}
        <style>{`
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes float {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-20px);
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.8;
      }
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-8px);
      }
    }
  `}</style>
      </section>

      {/* ABOUT US SECTION */}
      <section style={{
        backgroundColor: '#0a0a0a',
        padding: responsive ? '50px 20px' : '80px 40px',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : '1fr 1fr',
            gap: responsive ? '40px' : '80px',
            alignItems: 'center'
          }}>
            {/* Left Column - Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <span style={{
                color: SECONDARY_COLOR,
                fontSize: fontSize("14px", "12px"),
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                About Us
              </span>

              <h2 style={{
                fontSize: fontSize("48px", "32px"),
                fontWeight: '700',
                margin: '0',
                lineHeight: '1.3'
              }}>
                We Are Experts In Tax Consultancy
              </h2>

              <p style={{
                fontSize: fontSize("15px", "13px"),
                lineHeight: '1.8',
                color: '#e0e0e0',
                margin: '0'
              }}>
                With our strategic approach and deep tax industry knowledge, we deliver exceptional VAT consultancy services that help clients navigate compliance challenges and achieve their financial objectives in today's complex regulatory landscape.
              </p>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginTop: '16px'
              }}>
                <div style={{
                  width: '4px',
                  height: '60px',
                  backgroundColor: SECONDARY_COLOR,
                  borderRadius: '2px'
                }}></div>
                <div>
                  <p style={{
                    fontSize: fontSize("16px", "14px"),
                    fontWeight: '600',
                    margin: '0',
                    color: 'white'
                  }}>
                    Experience a Specialized and Dedicated TAX Consultancy.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Three Cards */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Vision Card */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                position: 'relative'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: PRIMARY_COLOR,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  position: 'absolute',
                  top: '-22px',
                  left: '24px'
                }}>
                  👁️
                </div>
                <div style={{ marginTop: '16px' }}>
                  <h3 style={{
                    fontSize: fontSize("18px", "16px"),
                    fontWeight: '700',
                    color: PRIMARY_COLOR,
                    margin: '0 0 12px 0'
                  }}>
                    Our Vision
                  </h3>
                  <p style={{
                    fontSize: fontSize("14px", "13px"),
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.6'
                  }}>
                    To be the most trusted tax advisory partner for businesses and individuals seeking clarity and resolution in an increasingly complex tax environment.
                  </p>
                </div>
              </div>

              {/* Mission Card */}
              <div style={{
                backgroundColor: SECONDARY_COLOR,
                borderRadius: '12px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                position: 'relative'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: PRIMARY_COLOR,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  position: 'absolute',
                  top: '-22px',
                  left: '24px'
                }}>
                  🎯
                </div>
                <div style={{ marginTop: '16px' }}>
                  <h3 style={{
                    fontSize: fontSize("18px", "16px"),
                    fontWeight: '700',
                    color: 'white',
                    margin: '0 0 12px 0'
                  }}>
                    Our Mission
                  </h3>
                  <p style={{
                    fontSize: fontSize("14px", "13px"),
                    color: 'white',
                    margin: '0',
                    lineHeight: '1.6'
                  }}>
                    To provide accessible, responsive tax counsel that empowers our clients to make confident decisions and achieve favorable outcomes in all their VAT and tax matters.
                  </p>
                </div>
              </div>

              {/* Values Card */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                position: 'relative'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: PRIMARY_COLOR,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  position: 'absolute',
                  top: '-22px',
                  left: '24px'
                }}>
                  💎
                </div>
                <div style={{ marginTop: '16px' }}>
                  <h3 style={{
                    fontSize: fontSize("18px", "16px"),
                    fontWeight: '700',
                    color: PRIMARY_COLOR,
                    margin: '0 0 12px 0'
                  }}>
                    Our Values
                  </h3>
                  <p style={{
                    fontSize: fontSize("14px", "13px"),
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.6'
                  }}>
                    Excellence in service, integrity in practice, and commitment to building lasting relationships through transparent communication and measurable tax savings results.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* UNLOCK SECTION */}
      <section style={{
        backgroundColor: PRIMARY_COLOR,
        color: 'white',
        padding: responsive ? '50px 20px' : '80px 40px',
        marginTop: responsive ? '70px' : '100px',
        paddingTop: responsive ? '70px' : '100px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <span style={{
            color: SECONDARY_COLOR,
            fontSize: fontSize("14px", "12px"),
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Say goodbye to VAT compliance headaches
          </span>

          <h2 style={{
            fontSize: fontSize("48px", "28px"),
            fontWeight: '700',
            margin: '16px 0 32px 0',
            lineHeight: '1.3'
          }}>
            Unlock Effortless VAT Compliance for Your Business - Powered by Our Technology and Expertise
          </h2>

          <p style={{
            fontSize: fontSize("15px", "13px"),
            lineHeight: '1.8',
            maxWidth: '800px',
            margin: '0 auto 60px',
            opacity: 0.95
          }}>
            Millions of businesses struggle with VAT compliance errors, delayed filings, and non-compliance penalties. NBR continues to introduce increasingly stringent VAT controls and automated matching systems. We provide expert consultancy and technology-driven solutions ensuring your complete VAT compliance across all regions and business activities.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : 'repeat(4, 1fr)',
            gap: responsive ? '30px' : '40px'
          }}>
            {[
              { icon: '🏆', label: '15+ Years of Combined Experience', desc: 'Our team consists of experienced VAT consultants and tax experts in Bangladesh.' },
              { icon: '🌍', label: 'Expertise Across All Industries', desc: 'Complete VAT consultancy for manufacturing, trading, services, and export/import sectors.' },
              { icon: '⚙️', label: 'Advanced VAT Technology', desc: 'We use cutting-edge technology to automate VAT filing, ensuring 100% compliance.' },
              { icon: '✅', label: '100% Compliance Guarantee', desc: 'Expert guidance in achieving seamless VAT compliance every single time.' }
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{ fontSize: '40px' }}>{item.icon}</div>
                <h3 style={{
                  fontSize: fontSize("14px", "12px"),
                  fontWeight: '600',
                  margin: '0',
                  lineHeight: '1.4'
                }}>
                  {item.label}
                </h3>
                <p style={{
                  fontSize: fontSize("12px", "11px"),
                  margin: '0',
                  opacity: 0.85,
                  lineHeight: '1.5'
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS SECTION - REDESIGNED */}
      <section style={{
        backgroundColor: 'white',
        padding: responsive ? '60px 20px' : '100px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '-150px',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, rgba(245, 166, 35, 0.08) 0%, transparent 70%)`,
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite',
          pointerEvents: 'none'
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '-150px',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, rgba(31, 37, 72, 0.06) 0%, transparent 70%)`,
          borderRadius: '50%',
          animation: 'float 10s ease-in-out infinite 1s',
          pointerEvents: 'none'
        }}></div>

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: 'rgba(245, 166, 35, 0.15)',
              padding: '12px 24px',
              borderRadius: '50px',
              border: `1.5px solid ${SECONDARY_COLOR}`,
              marginBottom: '20px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <span style={{
                color: SECONDARY_COLOR,
                fontSize: fontSize("12px", "11px"),
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                🚀 OUR SERVICES
              </span>
            </div>

            <h2 style={{
              fontSize: fontSize("48px", "28px"),
              fontWeight: '800',
              color: PRIMARY_COLOR,
              margin: '0 0 16px 0',
              lineHeight: '1.2'
            }}>
              Comprehensive VAT Solutions
            </h2>

            <p style={{
              fontSize: fontSize("17px", "14px"),
              color: '#666',
              margin: '0',
              maxWidth: '600px',
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: '1.6'
            }}>
              From registration to compliance, we provide end-to-end VAT services tailored to your business needs
            </p>
          </div>

          {/* Solution 1 - VAT Registration */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : '1fr 1fr',
            gap: responsive ? '30px' : '60px',
            alignItems: 'center',
            marginBottom: '80px',
            animation: 'fadeInUp 0.8s ease-out 0.1s both'
          }}>
            {/* Image/Icon Box */}
            <div style={{
              backgroundColor: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 100%)',
              borderRadius: '20px',
              padding: responsive ? '40px 20px' : '0px 0px',
              minHeight: responsive ? '250px' : '450px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              border: '2px solid #f0f0f0',
              transition: 'all 0.4s ease',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 16px 40px rgba(245, 166, 35, 0.2)';
                e.currentTarget.style.borderColor = SECONDARY_COLOR;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.borderColor = '#f0f0f0';
              }}>
              {/* Background accent */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '300px',
                height: '300px',
                background: `radial-gradient(circle, ${SECONDARY_COLOR}20, transparent 70%)`,
                borderRadius: '50%',
                animation: 'float 6s ease-in-out infinite'
              }}></div>

              {/* Icon/Image Container */}
              <div style={{
                textAlign: 'center',
                position: 'relative',
                zIndex: 1
              }}>
                <img 
                  src={require('../../assets/images/easy_vat_reg.png')}
                  alt="VAT Registration"
                  style={{
                    width: responsive ? '100%' : '415px',
                    height: responsive ? 'auto' : '415px',
                    maxWidth: responsive ? '300px' : '415px',
                    objectFit: 'contain',
                    marginBottom: '20px'
                  }}
                />
              </div>
            </div>

            {/* Content Box */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                width: 'fit-content',
                backgroundColor: `${SECONDARY_COLOR}15`,
                padding: '8px 16px',
                borderRadius: '50px',
                border: `1px solid ${SECONDARY_COLOR}30`
              }}>
                <span style={{
                  color: SECONDARY_COLOR,
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  ✓ VAT Registration
                </span>
              </div>

              <h3 style={{
                fontSize: fontSize("36px", "24px"),
                fontWeight: '800',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.2'
              }}>
                Easy VAT Registration in Bangladesh
              </h3>

              <p style={{
                fontSize: fontSize("15px", "13px"),
                color: '#666',
                lineHeight: '1.9',
                margin: '0'
              }}>
                Get your business VAT registered quickly and efficiently. Our expert team handles all documentation and submission to NBR, ensuring compliance with all regulatory requirements. We guide you through every step of the registration process and ensure timely approval.
              </p>

              {/* Features List */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                marginTop: '12px'
              }}>
                {[
                  '7-14 day fast registration',
                  'Complete documentation support',
                  'NBR submission & follow-up',
                  '100% compliance guaranteed'
                ].map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    animation: `slideInLeft 0.6s ease-out ${0.15 + (i * 0.1)}s both`
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: SECONDARY_COLOR,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      ✓
                    </div>
                    <span style={{
                      fontSize: fontSize("14px", "13px"),
                      color: '#555',
                      fontWeight: '500'
                    }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div style={{ marginTop: '24px' }}>
                <button style={{
                  backgroundColor: SECONDARY_COLOR,
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: responsive ? '12px 24px' : '14px 32px',
                  fontSize: fontSize("14px", "13px"),
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: `0 8px 20px ${SECONDARY_COLOR}30`
                }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px) scale(1.05)';
                    e.target.style.boxShadow = `0 12px 30px ${SECONDARY_COLOR}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = `0 8px 20px ${SECONDARY_COLOR}30`;
                  }}>
                  Learn More <span style={{ fontSize: '16px' }}>→</span>
                </button>
              </div>
            </div>
          </div>

          {/* Solution 2 - Online VAT Filing */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : '1fr 1fr',
            gap: responsive ? '30px' : '60px',
            alignItems: 'center',
            marginBottom: '80px',
            animation: 'fadeInUp 0.8s ease-out 0.2s both'
          }}>
            {/* Content Box - Order 2 on desktop */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              order: responsive ? 'unset' : 2
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                width: 'fit-content',
                backgroundColor: `#4ECDC415`,
                padding: '8px 16px',
                borderRadius: '50px',
                border: `1px solid #4ECDC430`
              }}>
                <span style={{
                  color: '#4ECDC4',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  ✓ Online Filing
                </span>
              </div>

              <h3 style={{
                fontSize: fontSize("36px", "24px"),
                fontWeight: '800',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.2'
              }}>
                Streamlined Online VAT Return Filing
              </h3>

              <p style={{
                fontSize: fontSize("15px", "13px"),
                color: '#666',
                lineHeight: '1.9',
                margin: '0'
              }}>
                Manage your VAT returns online with our user-friendly platform. We provide complete support for VAT online filing, ensuring timely and accurate submission of returns to NBR. Our system integrates with your accounting software for seamless data flow and error-free compliance.
              </p>

              {/* Features List */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                marginTop: '12px'
              }}>
                {[
                  'Automated VAT calculation',
                  'Real-time return tracking',
                  'Accounting software integration',
                  'Audit-ready reports'
                ].map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    animation: `slideInLeft 0.6s ease-out ${0.25 + (i * 0.1)}s both`
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#4ECDC4',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      ✓
                    </div>
                    <span style={{
                      fontSize: fontSize("14px", "13px"),
                      color: '#555',
                      fontWeight: '500'
                    }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div style={{ marginTop: '24px' }}>
                <button style={{
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: responsive ? '12px 24px' : '14px 32px',
                  fontSize: fontSize("14px", "13px"),
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: `0 8px 20px #4ECDC430`
                }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px) scale(1.05)';
                    e.target.style.boxShadow = `0 12px 30px #4ECDC440`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = `0 8px 20px #4ECDC430`;
                  }}>
                  Learn More <span style={{ fontSize: '16px' }}>→</span>
                </button>
              </div>
            </div>

            {/* Image/Icon Box - Order 1 on desktop */}
            <div style={{
              backgroundColor: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 100%)',
              borderRadius: '20px',
              padding: responsive ? '40px 20px' : '60px 40px',
              minHeight: responsive ? '250px' : '350px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              border: '2px solid #f0f0f0',
              transition: 'all 0.4s ease',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
              order: responsive ? 'unset' : 1
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 16px 40px rgba(78, 205, 196, 0.2)';
                e.currentTarget.style.borderColor = '#4ECDC4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.borderColor = '#f0f0f0';
              }}>
              {/* Background accent */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '300px',
                height: '300px',
                background: `radial-gradient(circle, #4ECDC420, transparent 70%)`,
                borderRadius: '50%',
                animation: 'float 6s ease-in-out infinite'
              }}></div>

              {/* Icon/Image Container */}
              <div style={{
                textAlign: 'center',
                position: 'relative',
                zIndex: 1
              }}>
                <img 
                  src={require('../../assets/images/steamed_vat.png')}
                  alt="Online Filing Platform"
                  style={{
                    width: responsive ? '100%' : '415px',
                    height: responsive ? 'auto' : '415px',
                    maxWidth: responsive ? '300px' : '415px',
                    objectFit: 'contain',
                    marginBottom: '20px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Solution 3 - VAT Consultancy */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : '1fr 1fr',
            gap: responsive ? '30px' : '60px',
            alignItems: 'center',
            animation: 'fadeInUp 0.8s ease-out 0.3s both'
          }}>
            {/* Image/Icon Box */}
            <div style={{
              backgroundColor: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 100%)',
              borderRadius: '20px',
              padding: responsive ? '40px 20px' : '60px 40px',
              minHeight: responsive ? '250px' : '350px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              border: '2px solid #f0f0f0',
              transition: 'all 0.4s ease',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 16px 40px rgba(45, 177, 209, 0.2)';
                e.currentTarget.style.borderColor = '#45B7D1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.borderColor = '#f0f0f0';
              }}>
              {/* Background accent */}
              <div style={{
                position: 'absolute',
                bottom: '-50%',
                right: '-50%',
                width: '300px',
                height: '300px',
                background: `radial-gradient(circle, #45B7D120, transparent 70%)`,
                borderRadius: '50%',
                animation: 'float 6s ease-in-out infinite'
              }}></div>

              {/* Icon/Image Container */}
              <div style={{
                textAlign: 'center',
                position: 'relative',
                zIndex: 1
              }}>
                <img 
                  src={require('../../assets/images/expert_vat.png')}
                  alt="Expert Consultancy"
                  style={{
                    width: responsive ? '100%' : '415px',
                    height: responsive ? 'auto' : '415px',
                    maxWidth: responsive ? '300px' : '415px',
                    objectFit: 'contain',
                    marginBottom: '20px'
                  }}
                />
              </div>
            </div>

            {/* Content Box */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                width: 'fit-content',
                backgroundColor: `#45B7D115`,
                padding: '8px 16px',
                borderRadius: '50px',
                border: `1px solid #45B7D130`
              }}>
                <span style={{
                  color: '#45B7D1',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  ✓ Consultancy
                </span>
              </div>

              <h3 style={{
                fontSize: fontSize("36px", "24px"),
                fontWeight: '800',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.2'
              }}>
                Expert VAT Consultancy Services
              </h3>

              <p style={{
                fontSize: fontSize("15px", "13px"),
                color: '#666',
                lineHeight: '1.9',
                margin: '0'
              }}>
                Get professional VAT consultancy for strategic tax planning and compliance optimization. Our experts provide guidance on VAT treatment, input VAT recovery, audit preparation, and dispute resolution with NBR. We ensure your business maintains full compliance while optimizing tax efficiency.
              </p>

              {/* Features List */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                marginTop: '12px'
              }}>
                {[
                  'Strategic tax planning',
                  'Input VAT recovery optimization',
                  'NBR audit preparation',
                  'Dispute resolution support'
                ].map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    animation: `slideInLeft 0.6s ease-out ${0.35 + (i * 0.1)}s both`
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#45B7D1',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      ✓
                    </div>
                    <span style={{
                      fontSize: fontSize("14px", "13px"),
                      color: '#555',
                      fontWeight: '500'
                    }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div style={{ marginTop: '24px' }}>
                <button style={{
                  backgroundColor: '#45B7D1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: responsive ? '12px 24px' : '14px 32px',
                  fontSize: fontSize("14px", "13px"),
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: `0 8px 20px #45B7D130`
                }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px) scale(1.05)';
                    e.target.style.boxShadow = `0 12px 30px #45B7D140`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = `0 8px 20px #45B7D130`;
                  }}>
                  Learn More <span style={{ fontSize: '16px' }}>→</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced CSS Animations */}
        <style>{`
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(40px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes float {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-20px);
      }
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-15px);
      }
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }

    @keyframes swing {
      0%, 100% {
        transform: rotateZ(-2deg);
      }
      50% {
        transform: rotateZ(2deg);
      }
    }
  `}</style>
      </section>

      {/* WHY WORK WITH US SECTION */}
      <section style={{
        backgroundColor: 'white',
        padding: responsive ? '50px 20px' : '80px 40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#d4e8e6',
              padding: '8px 16px',
              borderRadius: '20px',
              marginBottom: '20px'
            }}>
              <span style={{
                color: PRIMARY_COLOR,
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Why work with JK Associates?
              </span>
            </div>

            <h2 style={{
              fontSize: fontSize("40px", "24px"),
              fontWeight: '700',
              color: PRIMARY_COLOR,
              margin: '0 0 16px 0',
              lineHeight: '1.3'
            }}>
              What makes us the right partner for VAT registration and consultancy
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : 'repeat(3, 1fr)',
            gap: responsive ? '20px' : '24px',
            marginBottom: '30px'
          }}>
            {/* Card 1 */}
            <div style={{
              backgroundColor: PRIMARY_COLOR,
              color: 'white',
              borderRadius: '16px',
              padding: responsive ? '30px 20px' : '40px 30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 16px 32px rgba(31, 37, 72, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{
                fontSize: '32px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                👥
              </div>
              <h3 style={{
                fontSize: fontSize("18px", "16px"),
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                Team of VAT Experts
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                margin: '0',
                lineHeight: '1.6',
                opacity: 0.95
              }}>
                With 15+ years of combined experience in VAT regulations and Bangladesh tax laws, we handle your VAT compliance needs with professionalism and expertise.
              </p>
            </div>

            {/* Card 2 */}
            <div style={{
              backgroundColor: PRIMARY_COLOR,
              color: 'white',
              borderRadius: '16px',
              padding: responsive ? '30px 20px' : '40px 30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 16px 32px rgba(31, 37, 72, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{
                fontSize: '32px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                🏆
              </div>
              <h3 style={{
                fontSize: fontSize("18px", "16px"),
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                Advanced VAT Technology
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                margin: '0',
                lineHeight: '1.6',
                opacity: 0.95
              }}>
                We use leading technology solutions for automating VAT registration, online filing, compliance tracking, and audit management for different business types.
              </p>
            </div>

            {/* Card 3 */}
            <div style={{
              backgroundColor: PRIMARY_COLOR,
              color: 'white',
              borderRadius: '16px',
              padding: responsive ? '30px 20px' : '40px 30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 16px 32px rgba(31, 37, 72, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{
                fontSize: '32px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                🔍
              </div>
              <h3 style={{
                fontSize: fontSize("18px", "16px"),
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                Transparent & Affordable
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                margin: '0',
                lineHeight: '1.6',
                opacity: 0.95
              }}>
                We offer transparent pricing with flexible consultation packages and competitive rates. Full visibility on your VAT compliance status and service progress.
              </p>
            </div>

            {/* Card 4 */}
            <div style={{
              backgroundColor: PRIMARY_COLOR,
              color: 'white',
              borderRadius: '16px',
              padding: responsive ? '30px 20px' : '40px 30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 16px 32px rgba(31, 37, 72, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{
                fontSize: '32px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                💼
              </div>
              <h3 style={{
                fontSize: fontSize("18px", "16px"),
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                500+ VAT Registrations
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                margin: '0',
                lineHeight: '1.6',
                opacity: 0.95
              }}>
                Successfully registered and managed VAT compliance for 500+ businesses across manufacturing, trading, services, and export/import sectors in Bangladesh.
              </p>
            </div>

            {/* Card 5 */}
            <div style={{
              backgroundColor: PRIMARY_COLOR,
              color: 'white',
              borderRadius: '16px',
              padding: responsive ? '30px 20px' : '40px 30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 16px 32px rgba(31, 37, 72, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{
                fontSize: '32px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                🌍
              </div>
              <h3 style={{
                fontSize: fontSize("18px", "16px"),
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                Local Expertise
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                margin: '0',
                lineHeight: '1.6',
                opacity: 0.95
              }}>
                With deep knowledge of NBR regulations and local business practices, we ensure your VAT compliance aligns with Bangladesh tax requirements and best practices.
              </p>
            </div>

            {/* CTA Card */}
            <div style={{
              backgroundColor: SECONDARY_COLOR,
              color: 'white',
              borderRadius: '16px',
              padding: responsive ? '30px 20px' : '40px 30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifyContent: 'center',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 16px 32px rgba(245, 166, 35, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{
                fontSize: '32px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                ✉️
              </div>
              <h3 style={{
                fontSize: fontSize("18px", "16px"),
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                Connect with Us Today!
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                margin: '0 0 16px 0',
                lineHeight: '1.6'
              }}>
                Get in touch with our VAT consultants to discuss your registration and compliance needs.
              </p>
              <button
                style={{
                  backgroundColor: PRIMARY_COLOR,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  alignSelf: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.85';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
                onClick={() => window.open('https://api.whatsapp.com/send/?phone=+8801511404971&text=I%20am%20interested%20in%20VAT%20Registration%20and%20Consultancy%20services', '_blank')}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section style={{
        backgroundColor: '#f5f5f5',
        padding: responsive ? '50px 20px' : '80px 40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: fontSize("40px", "24px"),
            fontWeight: '700',
            color: PRIMARY_COLOR,
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            Frequently Asked Questions
          </h2>
          <Faq items={faqItems} />
        </div>
      </section>

      {/* OUR SERVICES SECTION */}
      <section style={{
        backgroundColor: 'white',
        padding: responsive ? '50px 20px' : '80px 40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '60px' }}>
            <p style={{
              color: ACCENT_COLOR,
              fontSize: fontSize("14px", "12px"),
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '0 0 16px 0'
            }}>
              Our services
            </p>
            <h2 style={{
              fontSize: fontSize("48px", "28px"),
              fontWeight: '700',
              color: PRIMARY_COLOR,
              margin: '0 0 16px 0',
              lineHeight: '1.3'
            }}>
              Comprehensive VAT Solutions
            </h2>
            <p style={{
              fontSize: fontSize("16px", "14px"),
              color: GRAY_DARK,
              margin: '0',
              maxWidth: '600px'
            }}>
              End-to-end VAT and taxation services tailored to your business needs
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : 'repeat(3, 1fr)',
            gap: responsive ? '30px' : '40px'
          }}>
            {/* Service 1: Customs Support */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                🚢
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Customs Support
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Expert guidance on customs clearance, documentation, and import-export compliance procedures.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 2: Bond Support */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                🔐
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Bond Support
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Assistance with bonding requirements, bond documentation, and regulatory compliance for duty-free operations.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 3: VAT Return Filing */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                📊
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                VAT Return Filing
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Timely and accurate VAT return preparation and filing with NBR to ensure full compliance and accuracy.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 4: Advisory */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                💡
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Advisory
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Strategic VAT planning and tax optimization guidance to minimize tax liability and maximize efficiency.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 5: Compliance */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                ✅
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Compliance
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Ensure regulatory compliance with VAT regulations, documentation standards, and ongoing audit readiness.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 6: Audit Support */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                🔍
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Audit Support
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Comprehensive audit preparation and NBR representation to confidently navigate VAT audits.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 7: VAT Tribunal */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                ⚖️
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                VAT Tribunal
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Expert representation in VAT tribunal proceedings and dispute resolution with tax authorities.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 8: Training */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                📚
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Training
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Comprehensive training programs for your team on VAT regulations, best practices, and compliance procedures.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>

            {/* Service 9: Taxation Support */}
            <div style={{
              position: 'relative',
              padding: responsive ? '30px 20px' : '40px 30px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              border: '1px solid #e5eaf2',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '0',
                height: '0',
                borderLeft: '50px solid transparent',
                borderTop: `50px solid ${ACCENT_COLOR}`
              }}></div>

              <div style={{
                fontSize: '40px',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
              }}>
                💼
              </div>
              <h3 style={{
                fontSize: fontSize("20px", "18px"),
                fontWeight: '700',
                color: PRIMARY_COLOR,
                margin: '0',
                lineHeight: '1.3'
              }}>
                Taxation Support
              </h3>
              <p style={{
                fontSize: fontSize("14px", "13px"),
                color: GRAY_DARK,
                margin: '0',
                lineHeight: '1.6'
              }}>
                Holistic taxation support including income tax, corporate tax, and integrated tax planning strategies.
              </p>
              <a href="#" style={{
                color: PRIMARY_COLOR,
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginTop: '8px',
                display: 'inline-block'
              }}>
                LEARN MORE →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section style={{
        backgroundColor: 'white',
        padding: responsive ? '50px 20px' : '80px 40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: fontSize("40px", "24px"),
            fontWeight: '700',
            color: PRIMARY_COLOR,
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            What Our Clients Say
          </h2>
          <p style={{
            fontSize: fontSize("15px", "13px"),
            color: '#666',
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            Join hundreds of satisfied businesses that have successfully registered for VAT and maintained compliance with JK Associates
          </p>
          <Testimonial isResponsive={responsive} />
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section style={{
        backgroundColor: PRIMARY_COLOR,
        color: 'white',
        padding: responsive ? '50px 20px' : '80px 40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: responsive ? '1fr' : '1fr 1fr',
            gap: responsive ? '30px' : '60px',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <span style={{
                color: SECONDARY_COLOR,
                fontSize: fontSize("14px", "12px"),
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Take Action Today
              </span>

              <h2 style={{
                fontSize: fontSize("48px", "28px"),
                fontWeight: '700',
                margin: '0',
                lineHeight: '1.3'
              }}>
                Ready to Get VAT Registered?
              </h2>

              <p style={{
                fontSize: fontSize("15px", "13px"),
                lineHeight: '1.8',
                margin: '0',
                opacity: 0.95
              }}>
                Start your VAT registration journey with JK Associates today. Our expert consultants are ready to guide you through registration, online filing, and ongoing compliance support.
              </p>

              <div style={{
                display: 'flex',
                gap: '16px',
                flexDirection: responsive ? 'column' : 'row',
                marginTop: '16px'
              }}>
                <button
                  style={{
                    backgroundColor: SECONDARY_COLOR,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: responsive ? '14px 28px' : '16px 40px',
                    fontSize: fontSize("16px", "14px"),
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.boxShadow = '0 8px 20px rgba(245, 166, 35, 0.4)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.transform = 'translateY(0)';
                  }}
                  onClick={() => window.open('https://api.whatsapp.com/send/?phone=+8801511404971&text=I%20am%20interested%20in%20VAT%20Registration%20and%20Consultancy%20services', '_blank')}
                >
                  Get VAT Registration Now <FaArrowRight size={14} />
                </button>

                <button
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '2px solid white',
                    borderRadius: '6px',
                    padding: responsive ? '14px 28px' : '16px 40px',
                    fontSize: fontSize("16px", "14px"),
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => window.location.href = '/about-us'}
                >
                  Learn More
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: responsive ? '1fr 1fr' : 'repeat(3, 1fr)',
                gap: '20px',
                marginTop: '32px',
                paddingTop: '32px',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                {[
                  { number: '500+', label: 'VAT Registrations' },
                  { number: '98%', label: 'Success Rate' },
                  { number: '15+', label: 'Years Experience' }
                ].map((stat, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{
                      fontSize: fontSize("24px", "18px"),
                      fontWeight: '700',
                      color: SECONDARY_COLOR
                    }}>
                      {stat.number}
                    </div>
                    <div style={{
                      fontSize: fontSize("12px", "11px"),
                      color: '#e0e0e0',
                      fontWeight: '500'
                    }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!responsive && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}>
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '24px',
                  border: `1px solid rgba(245, 166, 35, 0.3)`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: SECONDARY_COLOR,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    ⚡
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      margin: '0 0 8px 0',
                      color: 'white'
                    }}>
                      Fast Registration
                    </h4>
                    <p style={{
                      fontSize: '12px',
                      color: '#e0e0e0',
                      margin: '0'
                    }}>
                      Quick VAT registration in 7-14 days
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '24px',
                  border: `1px solid rgba(245, 166, 35, 0.3)`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: SECONDARY_COLOR,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    🛡️
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      margin: '0 0 8px 0',
                      color: 'white'
                    }}>
                      Full Compliance
                    </h4>
                    <p style={{
                      fontSize: '12px',
                      color: '#e0e0e0',
                      margin: '0'
                    }}>
                      100% NBR compliance guaranteed
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '24px',
                  border: `1px solid rgba(245, 166, 35, 0.3)`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: SECONDARY_COLOR,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    💰
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      margin: '0 0 8px 0',
                      color: 'white'
                    }}>
                      Affordable Packages
                    </h4>
                    <p style={{
                      fontSize: '12px',
                      color: '#e0e0e0',
                      margin: '0'
                    }}>
                      Competitive pricing for all business sizes
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '24px',
                  border: `1px solid rgba(245, 166, 35, 0.3)`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: SECONDARY_COLOR,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    👥
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      margin: '0 0 8px 0',
                      color: 'white'
                    }}>
                      Expert Support
                    </h4>
                    <p style={{
                      fontSize: '12px',
                      color: '#e0e0e0',
                      margin: '0'
                    }}>
                      24/7 dedicated VAT consultants
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}